import { createContext } from 'react';
import {
	IItemForGetShortInfo,
	TFileId,
	IEditTeaser,
	TTeaserContentTypes,
	TTeaserControlPoints,
	ITeaserDishTransition,
} from 'types';

export type TEditTeaserVisibleForm = 'mainForm' | 'translations';
export type TSourceUrlLocally = Nullable<string>;
export type TEditTeaserSelectedContentType = TTeaserContentTypes | null;
export type TEditTeaserTransitionType = 'dish' | 'game' | null;
export type TEditTeaserControlPoint = TTeaserControlPoints | null;
export type TTypeOfEditTeaserModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'imageFileSizeExceeded'
	| 'videoFileSizeExceeded'
	| 'fileUploadError'
	| 'invalidFileFormat';
export interface IEditTeaserTotalSchedule {
	id: string;
	value: string;
}

export interface IInitialFormData {
	filePath: string;
	selectedCustomer: Nullable<string>;
	selectedRestaurants: string[];
	selectedContentType: TEditTeaserSelectedContentType;
	description: string;
	schedule: string;
	totalSchedules: IEditTeaserTotalSchedule[];
	selectedTransitionType: TEditTeaserTransitionType;
	selectedTransitionDishCategory: Nullable<string>;
	selectedTransitionDishSubCategory: Nullable<string>;
	selectedTransitionDishName: Nullable<string>;
	selectedTransitionGameCategory: Nullable<string>;
	selectedTransitionGameName: Nullable<string>;
	selectedControlPoint: TEditTeaserControlPoint;
	delayTime: string;
	launchCount: string;
	countDown: string;
}

export interface IEditTeaserState {
	needRefetchData: boolean;
	initialFormData: IInitialFormData | null;
	visibleForm: TEditTeaserVisibleForm;
	teaserID: string;
	filePath: string | null;
	fileId: TFileId;
	sourceUrlLocally: TSourceUrlLocally;
	fetchedCustomers: IItemForGetShortInfo[];
	selectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfo[];
	selectedRestaurants: string[];
	selectedContentType: TEditTeaserSelectedContentType;
	description: string;
	schedule: string;
	totalSchedules: IEditTeaserTotalSchedule[];
	selectedTransitionType: TEditTeaserTransitionType;
	dishTransition: Nullable<ITeaserDishTransition>;
	fetchedDishCategories: IItemForGetShortInfo[];
	selectedTransitionDishCategory: Nullable<string>;
	fetchedDishSubCategories: IItemForGetShortInfo[];
	selectedTransitionDishSubCategory: Nullable<string>;
	fetchedDishes: IItemForGetShortInfo[];
	selectedTransitionDishName: Nullable<string>;
	fetchedGameCategories: IItemForGetShortInfo[];
	selectedTransitionGameCategory: Nullable<string>;
	fetchedGames: IItemForGetShortInfo[];
	selectedTransitionGameName: Nullable<string>;
	selectedControlPoint: TEditTeaserControlPoint;
	delayTime: string;
	launchCount: string;
	countDown: string;
	dataForSubmit: IEditTeaser | null;
	typeOfModalWindow: TTypeOfEditTeaserModalWindow;
}

export const editTeaserInitialState: IEditTeaserState = {
	visibleForm: 'mainForm',
	needRefetchData: false,
	initialFormData: null,
	teaserID: '',
	fileId: null,
	filePath: null,
	sourceUrlLocally: null,
	fetchedCustomers: [],
	selectedCustomer: null,
	fetchedRestaurants: [],
	selectedRestaurants: [],
	selectedContentType: null,
	description: '',
	schedule: '',
	totalSchedules: [],
	selectedTransitionType: null,
	dishTransition: null,
	fetchedDishCategories: [],
	selectedTransitionDishCategory: null,
	fetchedDishSubCategories: [],
	selectedTransitionDishSubCategory: null,
	fetchedDishes: [],
	selectedTransitionDishName: null,
	fetchedGameCategories: [],
	selectedTransitionGameCategory: null,
	fetchedGames: [],
	selectedTransitionGameName: null,
	selectedControlPoint: null,
	delayTime: '',
	launchCount: '',
	countDown: '',
	dataForSubmit: null,
	typeOfModalWindow: null,
};

export interface IEditTeaserContext extends IEditTeaserState {
	refetchData(value: boolean): void;
	changeVisibleForm(value: TEditTeaserVisibleForm): void;
	setFile(fileId: TFileId, sourceUrlLocally: TSourceUrlLocally): void;
	deleteVideo(): void;
	setFetchedCustomers(value: IItemForGetShortInfo[]): void;
	setCustomer(value: Nullable<string>): void;
	setFetchedRestaurants(value: IItemForGetShortInfo[]): void;
	setRestaurants(value: string[]): void;
	setContentType(value: TEditTeaserSelectedContentType): void;
	changeDescription(value: string): void;
	changeSchedule(value: string): void;
	changeAdditionalSchedule(fieldId: string, value: string): void;
	setSchedules(value: IEditTeaserTotalSchedule[]): void;
	setTransitionType(value: TEditTeaserTransitionType): void;
	setFetchedDishCategories(value: IItemForGetShortInfo[]): void;
	setTransitionDishCategory(value: Nullable<string>): void;
	setFetchedDishSubCategories(value: IItemForGetShortInfo[]): void;
	setTransitionDishSubCategory(value: Nullable<string>): void;
	setFetchedDishes(value: IItemForGetShortInfo[]): void;
	setTransitionDishName(value: Nullable<string>): void;
	setFetchedGameCategories(value: IItemForGetShortInfo[]): void;
	setTransitionGameCategory(value: Nullable<string>): void;
	setFetchedGames(value: IItemForGetShortInfo[]): void;
	setTransitionGameName(value: Nullable<string>): void;
	setControlPoint(value: TEditTeaserControlPoint): void;
	changeDelayTime(value: string): void;
	changeLaunchCount(value: string): void;
	changeCountDown(value: string): void;
	setDataForSubmit(value: IEditTeaser | null): void;
	changeTypeOfModalWindow(value: TTypeOfEditTeaserModalWindow): void;
	resetFields(): void;
}

export const EditTeaserContext = createContext<IEditTeaserContext>({
	refetchData: () => undefined,
	changeVisibleForm: () => undefined,
	setFile: () => undefined,
	deleteVideo: () => undefined,
	setFetchedCustomers: () => undefined,
	setCustomer: () => undefined,
	setFetchedRestaurants: () => undefined,
	setRestaurants: () => undefined,
	setContentType: () => undefined,
	changeDescription: () => undefined,
	changeSchedule: () => undefined,
	changeAdditionalSchedule: () => undefined,
	setSchedules: () => undefined,
	setTransitionType: () => undefined,
	setFetchedDishCategories: () => undefined,
	setTransitionDishCategory: () => undefined,
	setFetchedDishSubCategories: () => undefined,
	setTransitionDishSubCategory: () => undefined,
	setFetchedDishes: () => undefined,
	setTransitionDishName: () => undefined,
	setFetchedGameCategories: () => undefined,
	setTransitionGameCategory: () => undefined,
	setFetchedGames: () => undefined,
	setTransitionGameName: () => undefined,
	setControlPoint: () => undefined,
	changeDelayTime: () => undefined,
	changeLaunchCount: () => undefined,
	changeCountDown: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	resetFields: () => undefined,
	...editTeaserInitialState,
});
